import { Quaternion, Vector3 } from 'three'

/**
 * Hand placements in tracking space, for inputs that can't freely pose two
 * hands (DESIGN.md §4). Positions are relative to a standing player whose head
 * is at (0, h, 0); the y values are written as fractions of standing height so
 * they scale with the calibrated pilot.
 *
 * These are the "is hovering learnable" test rig, not a VR emulation — the
 * desktop sim snaps between grips instead of pretending to be two 6-DOF hands.
 */
export interface GripPreset {
  id: string
  label: string
  /** [x, y/height, z] for the left hand; right is mirrored in x. */
  left: [number, number, number]
  right: [number, number, number]
  /** One line on what this grip should feel like — printed by the harness. */
  character: string
}

/**
 * The analytic grips sit at z = 0 — directly in the CoM's frontal plane — on
 * purpose. A forward hand offset adds a constant pitch torque under pure-up
 * thrust (hands 12 cm in front of the CoM pitch you backwards at hover), which
 * is a real and important effect but drowns out whatever a test is isolating.
 * `front` exists to measure that effect deliberately; everything else keeps it
 * at zero. The `y` values are fractions of standing height; the CoM sits at
 * 0.57, so `high` is genuinely above it and `low` genuinely below.
 */
export const GRIP_PRESETS: GripPreset[] = [
  {
    id: 'high',
    label: 'high',
    left: [-0.3, 0.92, 0],
    right: [0.3, 0.92, 0],
    character: 'hands above CoM — hanging pendulum, errors oscillate',
  },
  {
    id: 'mid',
    label: 'mid',
    left: [-0.32, 0.57, 0],
    right: [0.32, 0.57, 0],
    character: 'hands level with CoM — zero net torque, the neutral reference',
  },
  {
    id: 'low',
    label: 'low',
    left: [-0.26, 0.3, 0],
    right: [0.26, 0.3, 0],
    character: 'hands below CoM — inverted pendulum, errors grow',
  },
  {
    id: 'wide',
    label: 'wide',
    left: [-0.6, 0.57, 0],
    right: [0.6, 0.57, 0],
    character: 'long lever arms — big torque per newton, not more lift',
  },
  {
    id: 'narrow',
    label: 'narrow',
    left: [-0.12, 0.57, 0],
    right: [0.12, 0.57, 0],
    character: 'short lever arms — sluggish roll, forgiving',
  },
  {
    id: 'front',
    label: 'front',
    left: [-0.3, 0.57, -0.35],
    right: [0.3, 0.57, -0.35],
    character: 'arms out in front — up-thrust pitches you backwards',
  },
  {
    id: 'asym',
    label: 'asym',
    left: [-0.55, 0.95, 0],
    right: [0.2, 0.3, 0],
    character: 'one high and wide, one low and close — deliberately awkward',
  },
]

export function gripById(id: string): GripPreset {
  return GRIP_PRESETS.find((g) => g.id === id) ?? GRIP_PRESETS[0]
}

/** Resolve a grip's normalised hand slot into a tracking-space position. */
export function gripPosition(
  slot: [number, number, number],
  standingHeight: number,
  out: Vector3,
): Vector3 {
  return out.set(slot[0], slot[1] * standingHeight, slot[2])
}

/**
 * Nozzles pointing straight down, i.e. thrust straight up. The controller's
 * aim −Z is the exhaust, so this is the rotation taking +Z to +Y.
 */
export const NOZZLES_DOWN = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2)

/**
 * Aim a rocket by pitching/rolling away from straight-down.
 * @param pitch radians, positive tilts the thrust vector forward (−Z)
 * @param roll  radians, positive tilts the thrust vector to +X
 */
export function aimQuaternion(pitch: number, roll: number, out: Quaternion): Quaternion {
  out.copy(NOZZLES_DOWN)
  _tilt.setFromAxisAngle(_zAxis, -roll)
  out.premultiply(_tilt)
  _tilt.setFromAxisAngle(_xAxis, -pitch)
  out.premultiply(_tilt)
  return out
}

const _tilt = new Quaternion()
const _xAxis = new Vector3(1, 0, 0)
const _zAxis = new Vector3(0, 0, 1)
